import { useMemo, useState } from "react";
import { MktButton } from "./primitives";
import { useT } from "../../i18n";
import type { ZoneKey } from "./mkt-format";
import {
  buildOwnerActionBody,
  validateOwnerActionParams,
  type OwnerAction,
  type OwnerActionParams,
} from "./owner-console-logic";

const ZONES: ZoneKey[] = ["standard", "premium", "vip", "reserve", "grid_zero"];

const LABEL_KEY: Record<OwnerAction, string> = {
  release: "marketplace.owner.releaseLabel",
  relock: "marketplace.owner.relockLabel",
  private_sale: "marketplace.owner.privateSaleLabel",
};

function Field({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  type = "text",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label className="block space-y-1">
      <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-text-muted">{label}</span>
      <input
        data-testid={`owner-param-${id}`}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-3 rounded-md bg-elevated/60 border border-border-divider font-mono text-[12px] text-text-primary focus:outline-none focus:border-neon-teal/50"
      />
      {error && (
        <span data-testid={`owner-param-${id}-error`} className="block font-mono text-[9.5px] text-state-danger">
          {error}
        </span>
      )}
    </label>
  );
}

/**
 * Collects the AUTHORITATIVE integer parameters for a live owner action and, on
 * submit, hands the validated body + derived slots/zone to the confirmation
 * dialog. This is the fix for the missing-payload defect: no owner action can be
 * confirmed until the exact backend-required fields are present & well-typed.
 */
export function OwnerActionParamsForm({
  action,
  onCancel,
  onReady,
}: {
  action: OwnerAction;
  onCancel: () => void;
  onReady: (built: { body: Record<string, unknown>; slots: number; zone: ZoneKey }) => void;
}) {
  const t = useT();
  const [p, setP] = useState<OwnerActionParams>({ zone: "standard" });
  const [touched, setTouched] = useState(false);
  const v = useMemo(() => validateOwnerActionParams(action, p), [action, p]);
  const set = (patch: Partial<OwnerActionParams>) => setP((prev) => ({ ...prev, ...patch }));
  const err = (k: string) => (touched ? v.errors[k] : undefined);

  const submit = () => {
    setTouched(true);
    if (!v.ok) return;
    onReady({ body: buildOwnerActionBody(action, p), slots: v.slots, zone: v.zone });
  };

  const needsRect = action === "relock" || action === "private_sale";

  return (
    <div
      data-testid="owner-params-form"
      data-action={action}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
    >
      <div className="w-full max-w-md neon-glass-strong rounded-2xl border border-border-strong p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg text-text-primary">{t(LABEL_KEY[action])}</h3>
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-neon-teal/70">
            {v.slots > 0 ? `${v.slots} slots` : ""}
          </span>
        </div>

        {action === "release" && (
          <Field id="reservation-id" label="RESERVATION ID" type="number" value={p.reservationId ?? ""} onChange={(x) => set({ reservationId: x })} error={err("reservationId")} placeholder="e.g. 1" />
        )}

        {needsRect && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field id="x" label="X" type="number" value={p.x ?? ""} onChange={(x) => set({ x })} error={err("x")} />
              <Field id="y" label="Y" type="number" value={p.y ?? ""} onChange={(y) => set({ y })} error={err("y")} />
              <Field id="w" label="W" type="number" value={p.w ?? ""} onChange={(w) => set({ w })} error={err("w")} />
              <Field id="h" label="H" type="number" value={p.h ?? ""} onChange={(h) => set({ h })} error={err("h")} />
            </div>
            <label className="block space-y-1">
              <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-text-muted">ZONE</span>
              <select
                data-testid="owner-param-zone"
                value={p.zone ?? "standard"}
                onChange={(e) => set({ zone: e.target.value as ZoneKey })}
                className="w-full h-9 px-3 rounded-md bg-elevated/60 border border-border-divider font-mono text-[12px] text-text-primary focus:outline-none focus:border-neon-teal/50"
              >
                {ZONES.map((z) => (
                  <option key={z} value={z}>
                    {z.toUpperCase().replace("_", " ")}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {action === "private_sale" && (
          <>
            <Field id="sale-id" label="SALE ID" value={p.saleId ?? ""} onChange={(saleId) => set({ saleId })} error={err("saleId")} placeholder="label or 0x…" />
            <Field id="buyer" label="BUYER ADDRESS" value={p.buyer ?? ""} onChange={(buyer) => set({ buyer })} error={err("buyer")} placeholder="0x…" />
            <Field id="unit-price" label="UNIT PRICE (µUSDC)" type="number" value={p.unitPriceMicro ?? ""} onChange={(unitPriceMicro) => set({ unitPriceMicro })} error={err("unitPriceMicro")} placeholder="e.g. 1000000" />
            <Field
              id="expiry"
              label="EXPIRY (UTC)"
              type="datetime-local"
              value={p.expiryRealTs && /^\d+$/.test(p.expiryRealTs) ? new Date(Number(p.expiryRealTs) * 1000).toISOString().slice(0, 16) : ""}
              onChange={(local) => set({ expiryRealTs: local ? String(Math.floor(new Date(local).getTime() / 1000)) : "" })}
              error={err("expiryRealTs")}
            />
          </>
        )}

        <div className="flex items-center justify-end gap-3 pt-1">
          <MktButton variant="ghost" testId="owner-params-cancel" onClick={onCancel}>
            {t("marketplace.owner.cancel")}
          </MktButton>
          <MktButton variant="primary" testId="owner-params-next" disabled={touched && !v.ok} onClick={submit}>
            Continue
          </MktButton>
        </div>
      </div>
    </div>
  );
}
